import React, { memo, useState } from 'react'
import get from 'lodash/get'
import ControlledSingleSelect from 'transfix-form/components/ControlledSingleSelect'
import Card from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import InlineMessage, { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, capitalize } from 'transfix-ui/core'
import Grid from 'transfix-ui/core/Grid/Grid'
import Typography from 'transfix-ui/core/Typography'
import { EditingForm } from 'apps/marketplace/pages/shipment-details/components/EditingForm'
import { EditIcon } from 'transfix-icons'
import { DISPLAY } from 'constants/display'
import { Pill } from './Pill'
import { useShipperEdiGeneralSettingsForm } from './forms/ShipperEdiGeneralSettingsForm/useshipperEdiGeneralSettingsForm'
import { useUpdateShipperApplicationEdiConfigMutation } from './graphql/UpdateShipperApplicationEdiConfig'

interface IShipperEdiGeneralSettingsProps {
  ediEnabled?: boolean | null
  configId?: string | null
}

const BOOLEAN_OPTIONS = [
  { value: 'True', label: 'True' },
  { value: 'False', label: 'False' },
]

export const ShipperEdiGeneralSettingsCard = memo<IShipperEdiGeneralSettingsProps>(
  ({ configId, ediEnabled }) => {
    const [isEditing, setIsEditing] = useState<boolean>(false)
    const toggleEditing = () => setIsEditing(edit => !edit)

    const rows = [
      {
        key: 'ediEnabled',
        label: 'EDI Enabled',
        value: ediEnabled,
        options: BOOLEAN_OPTIONS,
      },
    ]
    const methods = useShipperEdiGeneralSettingsForm({})

    const [showError, setShowError] = useState<boolean>(false)
    const [updateShipperApplicationEdiConfig] = useUpdateShipperApplicationEdiConfigMutation()

    const submitHandler = methods.handleSubmit(async result => {
      setShowError(false)

      if (!configId) {
        setShowError(true)
      } else {
        const response = await updateShipperApplicationEdiConfig({
          variables: {
            input: {
              id: configId,
              ...result,
            },
          },
          refetchQueries: ['ShipperApplicationEdiConfig'],
        })

        if (get(response, 'data.updateShipperApplicationEdiConfig.errors.length')) {
          setShowError(true)
        } else {
          setIsEditing(false)
        }
      }
    })

    return (
      <EditingForm
        isEditing={isEditing}
        onCancel={toggleEditing}
        onSubmit={submitHandler}
        formMethods={methods}>
        <Card>
          <Box mb={2} display='flex' alignItems='center'>
            <Typography variant='h3'>{isEditing ? 'Edit General Settings' : 'General'}</Typography>
            <Box ml='auto'>
              <IconButton Icon={EditIcon} color='tertiary' onClick={toggleEditing} size='large' />
            </Box>
          </Box>
          {showError && (
            <Box my={1}>
              <InlineMessage
                type={INLINE_MESSAGE_VARIANTS.ALERT}
                message='Error editing the general settings. Try again. If the issue persists contact tech support.'
              />
            </Box>
          )}
          {rows.map((row, index) => (
            <div key={row.label.toLowerCase()}>
              <Grid container direction='row' justifyContent='space-between' alignItems='center'>
                <Grid item>
                  <Typography variant='body2' color='textPrimary'>
                    {row.label}
                  </Typography>
                </Grid>
                <Grid item>
                  {isEditing ? (
                    <ControlledSingleSelect
                      control={methods.control}
                      name={row.key}
                      defaultValue={row.value ? 'True' : 'False'}
                      SingleSelectProps={{
                        id: row.key,
                        dataTest: `${row.key}-single-select`,
                        options: row.options,
                        label: row.value ? 'True' : 'False',
                        clearable: false,
                      }}
                    />
                  ) : (
                    <Pill
                      variant={
                        typeof row.value === 'boolean'
                          ? `${row.value ? 'success' : 'error'}`
                          : 'info'
                      }
                      label={capitalize(row.value?.toString() || DISPLAY.empty)}
                    />
                  )}
                </Grid>
              </Grid>
              {index !== rows.length - 1 && <hr />}
            </div>
          ))}
        </Card>
      </EditingForm>
    )
  }
)
